import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import DefaultMap from '@/pages/notification/bio/DefaultMap.tsx'
import { format } from 'date-fns'
import ContactBadge from '@/component/badge/ContactBadge.tsx'
import { equalString, isEmpty } from '@/util/strings.ts'
import Input from '@/component/input/Input.tsx'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { apiPatchBioAlertAssignPerson, apiPatchBioAlertProcess } from '@/api/notification/patchBioAlert.ts'
import useQueryGetAlertBioDetail from '@/api/notification/getAlertBioDetail.ts'
import useQueryGetUserDetail from '@/api/auth/getUser.ts'
import useQueryGetMe from '@/api/auth/getMe.ts'
import { useNavigate } from 'react-router-dom'
import { BioAlertModalContext } from '@/pages/notification/bio/BioAlertModal.tsx'
import { BioAlertConfirmStatus } from '@/api/notification/getNotificationBio.ts'
import { FallDetectionResultType } from '@/api/notification/getNotificationFallDetection.ts'
import { AlertVitalStatus } from '@/api/notification/alertServerValues.ts'
import { useTranslation } from 'react-i18next'
import { useMapLocationStore } from '@/store/MapLocationStore.ts'
import LocationIcon from '@/assets/ic_alert_location.svg?react'
import PulseIcon from '@/assets/ic_pulse.svg?react'
import { ALERT_PROCESS_CONTENT_MAX_LENGTH } from '@/constant/alert.ts'
import { toast } from 'react-toastify'
import { getApiErrorMessage } from '@/api/api.ts'
import { isAlertStateConflict } from '@/util/emergencyAlertLiveSync.ts'
import { formatAlertLocation, hasAlertCoordinates } from '@/util/alertLocation.ts'
import {
    getAlertConfirmName,
    getAlertGenderName,
    getAlertVitalStatusName
} from '@/util/alertDisplay.ts'

export interface IProps {
    className?: string
}

const BioAlertDetail: React.FC<IProps> = ({ className }) => {
    const { t } = useTranslation()

    const { id, refetchList, refetchAndResetSelection } = useContext(BioAlertModalContext)
    const { data, refetch: refetchDetail } = useQueryGetAlertBioDetail(id)
    const { data: user } = useQueryGetUserDetail(data?.protectee?.id)
    const { data: me } = useQueryGetMe()
    const [isExpandedMap, setExpandedMap] = useState(false)
    const navigate = useNavigate()
    const { setTargetLocation } = useMapLocationStore()
    
    // 특정 이메일 계정인지 확인
    const isMaskingRequired = useMemo(() => {
        return me?.email === 'dbd_cm@clomental.com'
    }, [me?.email])
    
    // 마스킹 처리 함수
    const getMaskedName = (name?: string) => {
        if (!isMaskingRequired || !name) return name
        if (name.length <= 1) return name
        return name.charAt(0) + '**'
    }
    
    const getMaskedContact = (contact?: string) => {
        if (!isMaskingRequired || !contact) return contact
        
        // 모든 전화번호 앞 3자리만 보여주고 나머지는 * 처리
        const contactParts = contact.split('-')
        if (contactParts.length === 3) {
            return `${contactParts[0]}-****-****`
        }
        
        // 기타 패턴의 전화번호 처리
        if (contact.length > 3) {
            return contact.substring(0, 3) + contact.substring(3).replace(/./g, '*')
        }
        
        return contact
    }
    
    const getMaskedAddress = (address?: string) => {
        if (!isMaskingRequired || !address) return address
        return '****'
    }

    const alertLocation = data?.alert?.location
    const hasLocation = hasAlertCoordinates(alertLocation)

    const actionTypes: ISelectOption[] = useMemo(
        () => [
            {
                id: FallDetectionResultType.ETC,
                text: t('notification.bioModal.type.etc'),
                value: FallDetectionResultType.ETC
            },
            {
                id: FallDetectionResultType.EMERGENCY_119,
                text: t('notification.bioModal.type.119'),
                value: FallDetectionResultType.EMERGENCY_119
            },
            {
                id: FallDetectionResultType.MALFUNCTION,
                text: t('notification.bioModal.type.malfunction'),
                value: FallDetectionResultType.MALFUNCTION

            }
        ],
        [t]
    )

    const [manager, setManager] = useState('')
    const [actionDetail, setActionDetail] = useState('')
    const [actionTypeId, setActionTypeId] = useState<string>(FallDetectionResultType.ETC)
    const [isSaving, setIsSaving] = useState(false)
    const isActionDetailTooLong = actionDetail.length > ALERT_PROCESS_CONTENT_MAX_LENGTH


    const resyncAfterConflict = async (error: unknown) => {
        if (!isAlertStateConflict(error)) return
        await refetchDetail()
        refetchList()
    }

    const onClickAssign = async () => {
        if (!id) return

        try {
            const { code } = await apiPatchBioAlertAssignPerson(id, { manager_name: manager })
            if (code == 200) {
                await refetchDetail()
                refetchList()
            }
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('notification.process.saveError')))
            await resyncAfterConflict(error)
        }
    }

    const getGenderName = useCallback((value: string) => getAlertGenderName(t, 'bioModal', value), [t])

    const onClickProcessAction = async () => {
        if (!id) return
        if (!actionTypeId) return
        if (isEmpty(actionDetail) || isActionDetailTooLong || isSaving) {
            if (isActionDetailTooLong) {
                toast.error(t('notification.process.tooLong', { max: ALERT_PROCESS_CONTENT_MAX_LENGTH }))
            }
            return
        }

        setIsSaving(true)
        try {
            const { code, message } = await apiPatchBioAlertProcess(id, {
                task_result: actionTypeId,
                task_content: actionDetail
            })
            if (Number(code) !== 200) throw new Error(message || t('notification.process.saveError'))
            toast.success(t('notification.process.saveSuccess'))
            await refetchAndResetSelection()
        } catch (error) {
            const message = error instanceof Error ? error.message : t('notification.process.saveError')
            toast.error(message || t('notification.process.saveError'))
            await resyncAfterConflict(error)
        } finally {
            setIsSaving(false)
        }
    }

    const onClickLocationButton = useCallback(() => {
        const latitude = data?.alert?.location?.latitude
        const longitude = data?.alert?.location?.longitude
        
        if (
            typeof latitude === 'number' && 
            typeof longitude === 'number' && 
            !isNaN(latitude) && 
            !isNaN(longitude) &&
            isFinite(latitude) && 
            isFinite(longitude)
        ) {
            setTargetLocation({
                latitude: latitude,
                longitude: longitude
            })
            navigate('/dashboard')
        } else {
            console.warn('Invalid location coordinates:', { latitude, longitude })
        }
    }, [data?.alert?.location, setTargetLocation, navigate])

    useEffect(() => {
        setActionTypeId(data?.alert?.result || FallDetectionResultType.ETC)
        setActionDetail(data?.alert?.content || '')
        setManager(me?.name || '')

    }, [data, me])

    const canEditContent = useMemo(() => {
        const confirmStatus = data?.alert?.confirm

        switch (confirmStatus) {
            case BioAlertConfirmStatus.DONE:
                return true
            case BioAlertConfirmStatus.ASSIGN:
                return !!data?.alert?.isMyTask
            case  BioAlertConfirmStatus.WAIT:
            default:
                return false
        }
    }, [data])

    const getSignalTone = (status?: string) => {
        if (!status) return 'is-neutral'
        return status.includes(AlertVitalStatus.STABLE) || status.includes(AlertVitalStatus.NORMAL) ? 'is-normal' : 'is-alert'
    }

    if (!data) {
        return <div className={`${className || ''} bio-alert-detail bio-alert-detail--empty`}>
            <span><PulseIcon aria-hidden /></span>
            <strong>{t('notification.v2.bioDetail.emptyTitle')}</strong>
            <p>{t('notification.v2.bioDetail.emptyDescription')}</p>
        </div>
    }

    return <section className={`${className || ''} bio-alert-detail`}>
        <div className="bio-alert-detail__timeline">
            <div>
                <span>{t('notification.bio.table.emergency.column')}</span>
                <strong>{data.alert?.createdAt ? format(data.alert.createdAt, 'yyyy/MM/dd HH:mm') : '-'}</strong>
            </div>
            <div>
                <span>{t('notification.bio.table.confirmed.column')}</span>
                <strong>{data.alert?.checkedAt ? format(data.alert.checkedAt, 'yyyy/MM/dd HH:mm') : t('notification.v2.bioDetail.awaitingConfirm')}</strong>
            </div>
            <div className={`bio-alert-detail__confirm bio-alert-detail__confirm--${data.alert?.confirm === BioAlertConfirmStatus.WAIT ? 'wait' : data.alert?.confirm === BioAlertConfirmStatus.ASSIGN ? 'assign' : 'done'}`}>
                <span>{t('notification.v2.bioDetail.currentStatus')}</span>
                <strong>{getAlertConfirmName(t, data.alert?.confirm || BioAlertConfirmStatus.WAIT)}</strong>
            </div>
        </div>

        <div className="bio-alert-detail__grid">
            <div className="bio-alert-detail__person-card">
                <div className="bio-alert-detail__person">
                    <button type="button" onClick={() => navigate(`/user?id=${data.protectee?.id}`)}>
                        <span>{getMaskedName(data.protectee?.name)}</span>
                        <small>{getGenderName(data.protectee?.gender || '')} · {data.protectee?.birthDate ? format(data.protectee.birthDate, 'yy-MM-dd') : '-'}</small>
                    </button>
                    <a href={data.protectee?.contact ? `tel:${data.protectee.contact}` : undefined}>
                        {getMaskedContact(data.protectee?.contact) || t('common.empty')}
                    </a>
                </div>

                <div className="bio-alert-detail__signals">
                    <article className={`bio-alert-signal ${getSignalTone(data.alert?.nameDetail?.hrateStatus)}`}>
                        <div><span>HR</span><p>{t('notification.v2.bioDetail.heartRate')}</p></div>
                        <strong>{data.alert?.nameDetail?.hrate ?? '-'}<small>bpm</small></strong>
                        <p>{data.alert?.nameDetail?.hrateStatus ? getAlertVitalStatusName(t, data.alert.nameDetail.hrateStatus) : t('notification.v2.bioDetail.noStatus')}</p>
                    </article>
                    <article className={`bio-alert-signal ${getSignalTone(data.alert?.nameDetail?.respStatus)}`}>
                        <div><span>RR</span><p>{t('notification.v2.bioDetail.respiration')}</p></div>
                        <strong>{data.alert?.nameDetail?.resp ?? '-'}<small>rpm</small></strong>
                        <p>{data.alert?.nameDetail?.respStatus ? getAlertVitalStatusName(t, data.alert.nameDetail.respStatus) : t('notification.v2.bioDetail.noStatus')}</p>
                    </article>
                </div>

                <div className="bio-alert-detail__location">
                    <div className="bio-alert-detail__section-heading">
                        <span>LOC</span>
                        <div><strong>{t('notification.v2.bioDetail.locationTitle')}</strong><small>{t('notification.v2.bioDetail.locationDescription')}</small></div>
                    </div>
                    <div className="bio-alert-detail__location-row">
                        <button type="button" className="bio-alert-detail__address" onClick={() => hasLocation && setExpandedMap(!isExpandedMap)}>
                            {formatAlertLocation(alertLocation, t, getMaskedAddress)}
                        </button>
                        <div>
                            {data.alert?.location?.latitude && data.alert?.location?.longitude && (
                                <button type="button" onClick={onClickLocationButton}>
                                    <LocationIcon />
                                    {t('dashboard.notification.location')}
                                </button>
                            )}
                            {hasLocation && (
                                <button type="button" onClick={() => setExpandedMap(!isExpandedMap)}>
                                    {isExpandedMap ? t('notification.v2.map.close') : t('notification.v2.map.show')}
                                </button>
                            )}
                        </div>
                    </div>
                    {isExpandedMap && hasLocation && <DefaultMap
                        className="bio-alert-detail__map"
                        latitude={alertLocation.latitude}
                        longitude={alertLocation.longitude}
                    />}
                </div>

                <div className="bio-alert-detail__meta">
                    <div><span>{t('notification.bioModal.address')}</span><strong>{getMaskedAddress(user?.address) || t('common.empty')}</strong></div>
                    <div><span>{t('notification.v2.alertType')}</span><strong>{data.alert?.name || t('notification.v2.bioDetail.defaultAlertName')}</strong></div>
                </div>

                <div className="bio-alert-detail__section-heading">
                    <span>TEL</span>
                    <div><strong>{t('notification.bioModal.contact.title')}</strong><small>{t('notification.v2.bioDetail.contactsDescription')}</small></div>
                </div>
                <div className="bio-alert-detail__contacts">
                    <div><ContactBadge>{t('notification.bioModal.contact.me')}</ContactBadge><p>{getMaskedContact(data.protectee?.contact) || t('common.empty')}</p></div>
                    <div><ContactBadge>{t('notification.bioModal.contact.protector')}</ContactBadge><p>{getMaskedContact(data.protector?.contact) || t('common.empty')}</p></div>
                    <div><ContactBadge>{t('notification.bioModal.contact.acquaintance')}</ContactBadge><p>{getMaskedContact(data.protector?.secondaryContact) || t('common.empty')}</p></div>
                    <div><ContactBadge>{t('notification.bioModal.contact.admin')}</ContactBadge><p>{getMaskedContact(data.protector?.tertiaryContact) || t('common.empty')}</p></div>
                </div>

                <div className="bio-alert-detail__memo">
                    <span>{t('notification.bioModal.memo.title')}</span>
                    <p>{isMaskingRequired ? '***************************' : data.protectee?.memo || t('common.empty')}</p>
                </div>
            </div>

            <aside className="bio-alert-detail__response-card">
                <div className="bio-alert-detail__section-heading">
                    <span>ACT</span>
                    <div><strong>{t('notification.v2.bioDetail.responseTitle')}</strong><small>{t('notification.v2.bioDetail.responseDescription')}</small></div>
                </div>
                {!equalString(data.alert?.confirm, BioAlertConfirmStatus.WAIT) && <div className="bio-alert-detail__field">
                    <label>{t('notification.bioModal.manager.title')}</label>
                    <Input disabled className="w-full" value={getMaskedName(data.alert?.managerName) || ''} />
                </div>}
                <div className="bio-alert-detail__field">
                    <label>{t('notification.bioModal.type.title')}</label>
                    <Select
                        className="w-full"
                        value={actionTypes.find(item => item.id === actionTypeId)}
                        options={actionTypes}
                        disabled={!canEditContent}
                        onChanged={option => setActionTypeId(option?.id)}
                    />
                </div>
                <div className={`bio-alert-detail__process ${!canEditContent ? 'is-disabled' : ''}`}>
                    <label>{t('notification.bioModal.process.title')}</label>
                    <textarea
                        disabled={!canEditContent}
                        maxLength={ALERT_PROCESS_CONTENT_MAX_LENGTH}
                        value={actionDetail}
                        placeholder={t('notification.v2.bioDetail.processPlaceholder')}
                        onChange={event => setActionDetail(event.target.value)}
                        aria-describedby="bio-process-limit"
                    />
                    <div id="bio-process-limit" className={`mt-[6px] flex justify-between gap-[8px] text-[11px] ${isActionDetailTooLong ? 'text-red-600' : 'text-[#7A8798]'}`} aria-live="polite">
                        <span>{isActionDetailTooLong ? t('notification.process.tooLong', { max: ALERT_PROCESS_CONTENT_MAX_LENGTH }) : t('notification.process.limitGuide', { max: ALERT_PROCESS_CONTENT_MAX_LENGTH })}</span>
                        <strong>{actionDetail.length} / {ALERT_PROCESS_CONTENT_MAX_LENGTH}</strong>
                    </div>
                </div>
            </aside>
        </div>

        <footer className="bio-alert-detail__footer">
            {data.alert?.confirm === BioAlertConfirmStatus.WAIT && <div className="bio-alert-detail__assign">
                <input
                    placeholder={t('notification.bioModal.manager.input.placeholder')}
                    value={manager}
                    onChange={event => setManager(event.target.value)}
                />
                <BorderButton
                    text={t('notification.bioModal.manager.btn.title')}
                    onClick={onClickAssign}
                />
            </div>}
            {data.alert?.confirm === BioAlertConfirmStatus.ASSIGN && data.alert?.isMyTask && <SolidButton
                text={t('notification.bioModal.submit.confirm')}
                disabled={isEmpty(actionDetail) || isEmpty(actionTypeId) || !canEditContent || isActionDetailTooLong || isSaving}
                onClick={onClickProcessAction}
            />}
            {data.alert?.confirm === BioAlertConfirmStatus.ASSIGN && !data.alert?.isMyTask && <button className="bio-alert-detail__assigned" disabled>
                {t('notification.bioModal.manager.assign')}
            </button>}
            {data.alert?.confirm === BioAlertConfirmStatus.DONE && <SolidButton
                text={t('notification.bioModal.submit.save')}
                disabled={isEmpty(actionDetail) || isEmpty(actionTypeId) || isActionDetailTooLong || isSaving}
                onClick={onClickProcessAction}
            />}
        </footer>
    </section>
}

export default BioAlertDetail
